/**
 * Plan-bake staleness — decides when baked tiles still represent what the user
 * is looking at, and when the map must fall back to the low-resolution proxy.
 *
 * A bake is a server-side render of the source PDF, placed from the overlay's
 * 4 registration corners. Two kinds of change can invalidate it, and they are
 * NOT equivalent:
 *
 *   PLACEMENT changed (corners moved) → the tiles are now in the WRONG PLACE.
 *     Showing them would be crisp-but-wrong — worse than blurry — so the map
 *     reverts to the proxy image, which re-corners instantly, until a re-bake.
 *
 *   PARAMETERS changed (white threshold, page, source) → the tiles are still
 *     correctly placed, just outdated. They keep rendering while the proxy
 *     shows the pending edit, and the UI offers a re-bake.
 *
 * The distinction is why the fingerprint is split in two: `placementHash`
 * covers only the corners, `paramsHash` covers everything else.
 */

import type { Overlay, PlanBake } from '../stores/projectStore';

/** Coordinates are rounded before hashing so floating-point noise from a
 *  round-trip through JSON/D1 doesn't read as a real placement change.
 *  7 decimals ≈ 1 cm — far finer than any registration the user can drag. */
const COORD_PRECISION = 7;

function roundCoord(n: number): number {
  return Number(n.toFixed(COORD_PRECISION));
}

/** Stable string for the 4 placement corners. */
export function placementHash(bounds: Overlay['bounds']): string {
  if (!Array.isArray(bounds) || bounds.length !== 4) return 'invalid';
  return bounds.map((c) => `${roundCoord(c[0])},${roundCoord(c[1])}`).join('|');
}

/** Stable string for the erased areas. Separate from `paramsHash` because an
 *  erase is REMOVAL: tiles baked before it still show content the user deleted,
 *  which is the crisp-but-wrong case placement staleness already guards. Folded
 *  into the params hash it would rank as "outdated but fine to show", and the
 *  erase would appear to do nothing until a re-bake. */
function eraseHash(bake: PlanBake): string {
  return (bake.erasePolygons ?? [])
    .map((poly) => poly.map(([u, v]) => `${u.toFixed(6)},${v.toFixed(6)}`).join(' '))
    .join(';');
}

/** Stable string for everything else that changes the rendered pixels. Not
 *  exported: `bakeHash` is the only intended caller, and a second entry point
 *  would let a caller compare params while ignoring placement — the mistake
 *  this module exists to prevent.
 *
 *  The crop rect lives here rather than alongside the erase because a crop also
 *  moves the corners, so it already reads as a placement change. */
function paramsHash(bake: PlanBake, sourceR2Key: string | undefined): string {
  const crop = bake.cropRect
    ? `${bake.cropRect.u0},${bake.cropRect.v0},${bake.cropRect.u1},${bake.cropRect.v1}`
    : 'full';
  return [
    sourceR2Key ?? '',
    bake.pageIndex,
    bake.dpi,
    bake.whiteThreshold,
    roundCoord(bake.pageWidthPt),
    roundCoord(bake.pageHeightPt),
    crop,
  ].join('|');
}

/** The crop window currently in force, defaulting to the whole page. */
export function effectiveCrop(bake: PlanBake | undefined): {
  u0: number; v0: number; u1: number; v1: number;
} {
  return bake?.cropRect ?? { u0: 0, v0: 0, u1: 1, v1: 1 };
}

/**
 * Map a point expressed in the overlay's CURRENT quad (0-1, as the map editing
 * tools produce) into fractions of the original PDF page.
 *
 * The current quad shows only the cropped window of the page, so a point at
 * u=0.5 of the quad is at the middle of that WINDOW, not of the page. Without
 * this composition, cropping twice — or erasing after a crop — would record
 * shapes against the wrong frame and the bake would cut the wrong region.
 */
export function quadUvToPageUv(
  u: number,
  v: number,
  bake: PlanBake | undefined,
): [number, number] {
  const c = effectiveCrop(bake);
  return [c.u0 + u * (c.u1 - c.u0), c.v0 + v * (c.v1 - c.v0)];
}

/** The full fingerprint recorded in `bake.lastBaked.hash` when a bake starts.
 *  Three `::`-joined segments — placement, erased areas, everything else — so a
 *  mismatch can be attributed instead of just detected. */
export function bakeHash(overlay: Pick<Overlay, 'bounds' | 'sourceR2Key' | 'bake'>): string {
  if (!overlay.bake) return '';
  return [
    placementHash(overlay.bounds),
    eraseHash(overlay.bake),
    paramsHash(overlay.bake, overlay.sourceR2Key),
  ].join('::');
}

export type BakeState =
  /** No PDF source — a plain PNG/JPG overlay; baking does not apply. */
  | 'unavailable'
  /** Has a PDF source but has never been baked. */
  | 'never'
  /** A bake job is in flight. */
  | 'baking'
  /** Baked tiles match the current placement and parameters. */
  | 'baked'
  /** Parameters changed; tiles are still correctly placed but outdated. */
  | 'stale-params'
  /** Areas were erased after the bake; tiles still show what was removed. */
  | 'stale-erase'
  /** Placement changed; tiles would render in the wrong place. */
  | 'stale-placement'
  /** A failed bake was rolled back: earlier tiles are held, but the placement
   *  they were baked at is unknown, so they cannot be trusted to render. */
  | 'unverified'
  /** The last bake failed. */
  | 'error';

/** True when the overlay's source is a PDF that this pipeline can bake. */
export function isPlanOverlay(overlay: Pick<Overlay, 'sourceR2Key'>): boolean {
  return !!overlay.sourceR2Key && overlay.sourceR2Key.toLowerCase().endsWith('.pdf');
}

/**
 * Resolve what the UI should show for this overlay's bake.
 *
 * Note the deliberate ordering: an in-flight job reports 'baking' even when its
 * inputs have already drifted (the user kept editing while it ran) — the job
 * itself is what's happening now, and staleness re-evaluates when it lands.
 */
export function getBakeState(overlay: Overlay): BakeState {
  if (!isPlanOverlay(overlay)) return 'unavailable';
  if (overlay.tileStatus === 'generating' || overlay.tileStatus === 'pending') return 'baking';
  const last = overlay.bake?.lastBaked;
  if (!last || !overlay.tilesetId) {
    // Rolled back after a failure: tiles exist but are unverifiable. Reporting
    // 'never' here would erase the failure from the UI entirely.
    return overlay.bake?.needsRebake && overlay.tilesetId ? 'unverified' : 'never';
  }
  if (overlay.tileStatus === 'error') return 'error';
  if (overlay.tileStatus !== 'ready') return 'never';

  const current = bakeHash(overlay);
  if (current === last.hash) return 'baked';

  // Attribute the mismatch: placement and erase force the proxy fallback,
  // ordinary parameter drift does not.
  const parts = last.hash.split('::');
  if (placementHash(overlay.bounds) !== parts[0]) return 'stale-placement';
  // A two-segment fingerprint was written before erase existed, so nothing was
  // erased then — read it as the empty erase set rather than comparing against
  // that build's parameter segment, which would report a phantom 'stale-erase'
  // and demote a perfectly good bake to the proxy.
  const lastErase = parts.length >= 3 ? parts[1] : '';
  if (eraseHash(overlay.bake!) !== lastErase) return 'stale-erase';
  return 'stale-params';
}

/**
 * Whether the map may render this overlay from its baked tiles.
 *
 * Callers must still apply their own conditions (e.g. the overlay being
 * actively dragged renders as an image so the edit handles can follow it).
 */
export function canRenderBakedTiles(overlay: Overlay): boolean {
  if (overlay.tileStatus !== 'ready' || !overlay.tilesetId) return false;
  // Non-PDF overlays with tiles (a large PNG tiled by the ortho path) have no
  // bake fingerprint to check — they were never re-placed after tiling.
  if (!isPlanOverlay(overlay)) return true;
  // A plan's tiles are placed by warping to the corners the bake ran against,
  // so without a fingerprint there is no way to know they still belong where
  // they would be drawn. That happens after a failed re-bake, where the poller
  // restores the previous tileset but the placement it was baked at is no
  // longer known. Unverifiable ⇒ proxy: blurry beats confidently wrong.
  if (!overlay.bake?.lastBaked) return false;
  const state = getBakeState(overlay);
  return state !== 'stale-placement' && state !== 'stale-erase';
}

/** Short label for the layer list. Null when there is nothing worth saying. */
export function bakeStatusLabel(state: BakeState): string | null {
  switch (state) {
    case 'baking': return 'Baking…';
    case 'baked': return 'Full res';
    case 'stale-params': return 'Edits pending bake';
    case 'stale-erase': return 'Erased — re-bake';
    case 'stale-placement': return 'Moved — re-bake';
    case 'unverified': return 'Re-bake needed';
    case 'error': return 'Bake failed';
    default: return null;
  }
}
